import i18n from "i18next";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import type { SharingError, SharingErrorAction } from "@/lib/sharing/errors";

interface Props {
  error: SharingError;
  onAction: (action: SharingErrorAction) => void;
  onDismiss: () => void;
}

// Renders the spec §6 three-piece pattern: headline + body +
// at-least-one action. Sits inline at the top of whichever section
// the error originated from — the surface that opens it decides
// placement, this just owns the look.
export function SharingErrorBanner({ error, onAction, onDismiss }: Props) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-700/50 dark:bg-amber-900/20"
    >
      <IconAlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-[var(--lilia-text)]">{error.headline}</p>
        <p className="mt-0.5 text-[var(--lilia-text-muted)]">{error.body}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {error.actions.map((a, i) => (
            <Button
              key={a.kind}
              variant={i === 0 ? "primary" : "outline"}
              size="xs"
              onClick={() => onAction(a)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="-mr-1 -mt-1 rounded-md p-1 text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
        aria-label={i18n.t("sharing:errors.dismiss")}
      >
        <IconX size={14} />
      </button>
    </div>
  );
}
